import React, { ReactElement, useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { FiExternalLink } from "react-icons/fi";
import { GoVerified } from "react-icons/go";
import { StepProps } from "./base";
import { WorkflowService, useGetWorkflowServiceLazyQuery } from "../../../__generated__/api";
import Help, { ARTICLES } from "../../../components/help/Help";
import { WorkflowServiceInstall } from "../../administrator/sections/workflow-services/ViewService";
import useRouting, { buildUrl, Paths } from "../../../contexts/Routing";
import TimedMessage from "../../../components/timed-message/TimedMessage";
import Styles from "../Onboarding.module.scss";

const ZENDESK_ID = "urn:fq:workflow-service:fa31fd2428084d44aa65364eef8688ea";
const INTERCOM_SERVICE_ID = "urn:fq:workflow-service:bf8983320bff499487e4c4f16e29b2a7";
const SALESFORCE_SERVICE_ID = "urn:fq:workflow-service:370fdb80109240deb9c22fc8550dac63";
const JIRA_SERVICE_ID = "urn:fq:workflow-service:203d1783258d47a685ca2243cc57808d";

function createUrl(selectedId: string) {
    const url = new URL(
        buildUrl(
            Paths.ONBOARDING_STEP,
            { stepId: "ticketing-system" },
        ),
        window.location.href,
    );
    url.searchParams.append("serviceId", selectedId);
    return url.toString();
}

function InstallButton({
    serviceId, onInstallService, imageName, integrationName, isSelected,
}: {
    serviceId: string;
    onInstallService: (id: string) => void;
    imageName: string;
    integrationName: string;
    isSelected: boolean;
}): ReactElement {
    return (
        <div className="col-12 col-md">
            <Card className="d-flex flex-column h-100">
                <button
                    className={`btn btn-none text-start flex-grow-1 d-flex flex-column
                align-items-center hover-shadow ${isSelected ? "bg-blue-light text-white" : ""}`}
                    type="button"
                    onClick={() => onInstallService(serviceId)}
                >

                    <Card.Img
                        src={`https://static.foqal.io/library/${imageName}`}
                        className={`p-4 flex-grow-1 ${isSelected ? Styles["filter-white"] : ""}`}
                    />
                    {isSelected && (
                        <span className="text-white mt-n3 mb-3">
                            <GoVerified size="30px" />
                        </span>
                    )}
                </button>
            </Card>
            <div className="text-center w-100 mt-1 mb-3">
                <a
                    href={`https://foqal.io/integrations/${integrationName}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-link text-gray-500"
                >
                    <FiExternalLink />
                    {" "}
                    Learn More
                </a>
            </div>
        </div>
    );
}
export default function OnboardingStep(props: StepProps): ReactElement {
    const [selectedService, setSelectedService] = useState<WorkflowService>();
    const [getService] = useGetWorkflowServiceLazyQuery();
    const { getSearchParams, historyPushSearch } = useRouting();

    const searchParams = getSearchParams(Paths.ONBOARDING_STEP);
    const { error: installError, isSuccess: installSuccess, serviceId: installedServiceId } = searchParams;

    const installService = async (id: string) => {
        if (props.profile.installedWorkflowServiceId === id) {
            props.onNext(props.profile);
            return;
        }
        const service = await getService({
            variables: {
                id,
            },
        });
        if (service.data?.workflowService) {
            setSelectedService(service.data?.workflowService as WorkflowService);
        }
    };

    useEffect(() => {
        if (installSuccess) {
            props.onNext({
                ...props.profile,
                installedWorkflowServiceId: installedServiceId,
                isSelectedFoqalTicketing: false,
            });
        } else if (props.profile.isSelectedFoqalTicketing == null && props.profile.installedWorkflowServiceId == null) {
            props.onSaveProfile({
                ...props.profile,
                isSelectedFoqalTicketing: true,
            });
        }
    }, []);
    return (
        <div>

            {selectedService && (
                <WorkflowServiceInstall
                    service={selectedService}
                    isInstalling={selectedService != null}
                    onDone={() => setSelectedService(undefined)}
                    onCompletedUrl={createUrl(selectedService.id)}
                />
            )}

            <TimedMessage
                shouldShow={installError != null && installError !== ""}
                title="Error while installing"
                variant="danger"
                onClose={() => {
                    delete (searchParams as Partial<typeof searchParams>).error;
                    historyPushSearch(searchParams);
                }}
            >
                {installError}
            </TimedMessage>
            {props.profile.isExternalSupport ? (
                <Help title="Whats your ticketing system?" articleId={ARTICLES.TICKETING_SYSTEM_PAGE_EXTERNAL} />
            ) : (
                <Help title="Whats your ticketing system?" articleId={ARTICLES.TICKETING_SYSTEM_PAGE_INTERNAL} />
            )}

            <h2 className="text-center">Where do you want to work?</h2>
            <p>
                Do you already have a ticketing system you want to work out of, or just want to use Foqal?
            </p>

            <button
                onClick={() => props.onNext({ ...props.profile, isSelectedFoqalTicketing: true })}
                type="button"
                className={`card px-3 col-10 mx-auto shadow hover-shadow ${
                    props.profile.isSelectedFoqalTicketing ? "bg-blue-light text-white" : ""}`}
            >
                <div className="row g-0 text-start">
                    <div className="col-md-3">
                        <img
                            src={props.profile.isSelectedFoqalTicketing
                                ? "https://static.foqal.io/logos/agent/white/logo-white-big.svg"
                                : "https://static.foqal.io/logos/agent/blue/logo-256.png"}
                            className="w-100 mt-4"
                            alt="Foqal Agent logo"
                        />
                    </div>
                    <div className="col-md-9 align-self-center">
                        <div className="card-body">
                            <h4 className={`card-title ${props.profile.isSelectedFoqalTicketing ? "text-white" : ""}`}>Foqal</h4>
                            <p className="card-text">
                                Foqal Agent is a modern-day Slack and Web based ticketing system. You
                                can manage all your requests directly from Foqal Agent on the browser,
                                on your phone or on Slack. Since all devices and channels are synchronized,
                                you can start
                                {" "}
                                {props.profile.isExternalSupport ? "working with your customers " : "a request"}
                                on Slack and finish in the browser.
                            </p>
                        </div>
                    </div>
                </div>
            </button>

            <h4 className="my-5 text-center">I have a ticketing system...</h4>

            <div className="d-flex list-group flex-row w-100 gap-2 justify-content-around mb-3">
                <InstallButton
                    serviceId={ZENDESK_ID}
                    onInstallService={installService}
                    imageName="zendesk.svg"
                    integrationName="zendesk"
                    isSelected={props.profile.installedWorkflowServiceId === ZENDESK_ID}
                />

                {props.profile.isExternalSupport ? (
                    <>
                        <InstallButton
                            serviceId={INTERCOM_SERVICE_ID}
                            onInstallService={installService}
                            imageName="intercom.svg"
                            integrationName="intercom"
                            isSelected={props.profile.installedWorkflowServiceId === INTERCOM_SERVICE_ID}
                        />

                        <InstallButton
                            serviceId={SALESFORCE_SERVICE_ID}
                            onInstallService={installService}
                            imageName="salesforce.svg"
                            integrationName="salesforce"
                            isSelected={props.profile.installedWorkflowServiceId === SALESFORCE_SERVICE_ID}
                        />

                    </>

                ) : (

                    <InstallButton
                        serviceId={JIRA_SERVICE_ID}
                        onInstallService={installService}
                        imageName="jira-software.svg"
                        integrationName="jira"
                        isSelected={props.profile.installedWorkflowServiceId === JIRA_SERVICE_ID}
                    />

                )}

            </div>

        </div>
    );
}
